import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowLeft, Phone, Mail, MapPin, FileText, Plus, Edit2, Trash2, X } from 'lucide-react';
import { fmtPatron } from '../utils/dates';
import PolicyModal from '../components/policies/PolicyModal';

// Número en formato internacional para WhatsApp (Uruguay por defecto):
// "094 336 231" → 59894336231 · "59893496811" queda igual.
function numeroWhatsApp(tel) {
  let d = String(tel || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('00')) d = d.slice(2);
  if (d.startsWith('598')) return d.length >= 11 ? d : '';
  if (d.startsWith('0')) d = d.slice(1);
  if (d.length === 8) return '598' + d;          // celular o fijo uruguayo sin el 0
  return d.length >= 10 ? d : '';                // otro país, ya con su código
}
// En compu abre WhatsApp Web directo en la charla; en el celular, la app.
function linkWhatsApp(tel) {
  const n = numeroWhatsApp(tel);
  const movil = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  return movil ? `https://wa.me/${n}` : `https://web.whatsapp.com/send?phone=${n}`;
}


const STATUS_LABEL = { active: 'Vigente', endorsed: 'Endosada', cancelled: 'Cancelada', expired: 'Vencida' };
const STATUS_CLASS = { active: 'badge-green', endorsed: 'badge-blue', cancelled: 'badge-red', expired: 'badge-amber' };

function EditClientModal({ client, onClose }) {
  const { updateClient } = useApp();
  const [form, setForm] = useState({ ...client });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e) => { e.preventDefault(); updateClient(client.id, form); onClose(); };
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>Editar cliente</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group"><label className="form-label">Nombre *</label><input value={form.name} onChange={e => set('name', e.target.value)} required /></div>
              <div className="form-group"><label className="form-label">CI / RUT</label><input value={form.dni} onChange={e => set('dni', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Teléfono</label><input value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Email</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Dirección</label><input value={form.address} onChange={e => set('address', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Notas</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn btn-primary">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal fusionar clientes duplicados ────────────────────
function MergeModal({ client, onClose }) {
  const { clients, policies, updateClient, updatePolicy, deleteClient, showToast } = useApp();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [keepData, setKeepData] = useState('current');

  const others = clients
    .filter(c => c.id !== client.id)
    .filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.dni?.includes(search) || c.phone?.includes(search))
    .slice(0, 20);

  const currentPolicies = policies.filter(p => p.clientId === client.id);
  const otherPolicies = selected ? policies.filter(p => p.clientId === selected.id) : [];

  const handleMerge = async () => {
    if (!selected) return;
    const keep = keepData === 'current' ? client : selected;
    const discard = keepData === 'current' ? selected : client;

    // Reasignar todas las pólizas del descartado
    for (const p of policies.filter(pol => pol.clientId === discard.id)) {
      await updatePolicy(p.id, { clientId: keep.id });
    }
    // Combinar datos: completar campos vacíos
    await updateClient(keep.id, {
      name: keep.name,
      dni: keep.dni || discard.dni,
      phone: keep.phone || discard.phone,
      email: keep.email || discard.email,
      address: keep.address || discard.address,
      notes: [keep.notes, discard.notes].filter(Boolean).join(' | ') || '',
    });
    await deleteClient(discard.id);
    showToast(`✅ Clientes fusionados — ${keep.name}`);
    onClose();
    if (discard.id === client.id) navigate('/clients');
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 660 }}>
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>🔀 Fusionar duplicados</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
            Buscá el cliente duplicado de <strong>{client.name}</strong>. Todas las pólizas de ambos quedarán en un único cliente.
          </p>
          <div className="form-group">
            <label className="form-label">Buscar duplicado</label>
            <input value={search} onChange={e => { setSearch(e.target.value); setSelected(null); }} placeholder="Nombre, CI o teléfono..." />
          </div>

          {search.length >= 2 && (
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 16 }}>
              {others.length === 0
                ? <p style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>Sin resultados</p>
                : others.map(c => (
                  <div key={c.id} onClick={() => setSelected(c)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer',
                    background: selected?.id === c.id ? 'var(--accent-dim)' : 'transparent',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: selected?.id === c.id ? 'var(--accent)' : 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: selected?.id === c.id ? '#fff' : 'var(--accent)', flexShrink: 0 }}>
                      {c.name.charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{[c.dni, c.phone, c.email].filter(Boolean).join(' · ')} · {policies.filter(p => p.clientId === c.id).length} pólizas</div>
                    </div>
                    {selected?.id === c.id && <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}>✓</span>}
                  </div>
                ))
              }
            </div>
          )}

          {selected && (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>¿Qué datos conservar en el cliente final?</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                {[{key:'current',c:client,count:currentPolicies.length,label:'Cliente actual'},{key:'other',c:selected,count:otherPolicies.length,label:'Duplicado'}].map(({key,c,count,label}) => (
                  <div key={key} onClick={() => setKeepData(key)} style={{ padding: 14, borderRadius: 10, cursor: 'pointer', border: `2px solid ${keepData === key ? 'var(--accent)' : 'var(--border)'}`, background: keepData === key ? 'var(--accent-dim)' : 'var(--bg-input)' }}>
                    <div style={{ fontSize: 11, color: keepData === key ? 'var(--accent)' : 'var(--text-muted)', fontWeight: 700, marginBottom: 6 }}>{keepData === key ? '✓ ' : ''}{label}</div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {c.dni && <div>CI: {c.dni}</div>}
                      {c.phone && <div>Tel: {c.phone}</div>}
                      {c.email && <div>{c.email}</div>}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>{count} póliza{count !== 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--amber)' }}>
                ⚠️ Acción irreversible. Las <strong>{currentPolicies.length + otherPolicies.length} pólizas</strong> quedarán bajo <strong>{keepData === 'current' ? client.name : selected.name}</strong>. El duplicado se eliminará.
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
          <button onClick={handleMerge} disabled={!selected} className="btn btn-primary">🔀 Fusionar</button>
        </div>
      </div>
    </div>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const { clients, policies, deleteClient, updatePolicy, claims, debts, quotes, loading } = useApp();
  const navigate = useNavigate();
  const [editModal, setEditModal] = useState(false);
  const [policyModal, setPolicyModal] = useState(false);
  const [mergeModal, setMergeModal] = useState(false);

  const client = clients.find(c => c.id === id);
  if (!client) return <div className="page"><p>{loading || !clients.length ? 'Cargando…' : 'Cliente no encontrado.'}</p></div>;

  const clientPolicies = policies.filter(p => p.clientId === id);
  const active = clientPolicies.filter(p => p.status === 'active' || p.status === 'endorsed');
  // Integración: todo lo del cliente en un solo lugar.
  const clientClaims = (claims || []).filter(c => c.clientId === id);
  const policyNumbers = new Set(clientPolicies.map(p => p.number));
  const clientDebts = (debts || []).filter(d => d.clientId === id || (d.policyNumber && policyNumbers.has(d.policyNumber)));
  const clientQuotes = (quotes || []).filter(q => q.clientId === id || (q.clientName && client.name && q.clientName.toLowerCase() === client.name.toLowerCase()));

  const handleDelete = () => {
    if (window.confirm(`¿Eliminar a ${client.name}?`)) { deleteClient(id); navigate('/clients'); }
  };

  return (
    <div className="page fade-in">
      <div style={{ marginBottom: 24 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/clients')} style={{ marginBottom: 20 }}>
          <ArrowLeft size={13} /> Volver
        </button>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
              background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontWeight: 800, color: 'var(--accent)', fontSize: 22
            }}>{client.name.charAt(0)}</div>
            <div>
              <h1 className="page-title">{client.name}</h1>
              {client.dni && <p className="page-subtitle">CI/RUT: {client.dni}</p>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setMergeModal(true)}>🔀 Fusionar duplicado</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditModal(true)}><Edit2 size={13} /> Editar</button>
            <button className="btn btn-danger btn-sm" onClick={handleDelete}><Trash2 size={13} /> Eliminar</button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'start' }}>
        {/* Contact card */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>Contacto</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {client.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <a href={`tel:${client.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}><Phone size={14} color="var(--accent)" />{client.phone}</a>
                  {numeroWhatsApp(client.phone) && (
                    <a href={linkWhatsApp(client.phone)} target="_blank" rel="noopener noreferrer" title="Abrir la charla en WhatsApp Web"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#fff', background: '#25d366', borderRadius: 999, padding: '4px 11px', textDecoration: 'none', lineHeight: 1.4 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.4.8 3.2.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2l-.5-.2z"/></svg>
                      WhatsApp
                    </a>
                  )}
                </div>
              )}
              {client.email && <a href={`mailto:${client.email}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}><Mail size={14} color="var(--accent)" />{client.email}</a>}
              {client.address && <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--text-secondary)' }}><MapPin size={14} color="var(--accent)" style={{ flexShrink: 0, marginTop: 2 }} />{client.address}</div>}
            </div>
          </div>
          {client.notes && (
            <div className="card">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Notas</h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{client.notes}</p>
            </div>
          )}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>{active.length}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Pólizas activas</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-display)' }}>{clientPolicies.length}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total historial</div>
              </div>
            </div>
          </div>
        </div>

        {/* Policies */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700 }}>Pólizas</h2>
            <button className="btn btn-primary btn-sm" onClick={() => setPolicyModal(true)}><Plus size={13} /> Nueva póliza</button>
          </div>
          {clientPolicies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
              <FileText size={32} style={{ marginBottom: 10, opacity: 0.3 }} />
              <p>Sin pólizas aún</p>
              <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => setPolicyModal(true)}>Cargar primera póliza</button>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>N° Póliza</th><th>Tipo</th><th>Compañía</th><th>Cobertura</th><th>Vence</th><th>Estado</th><th></th></tr></thead>
                <tbody>
                  {clientPolicies.map(p => (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/policies/${p.id}`)}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.number}</td>
                      <td style={{ textTransform: 'capitalize' }}>{p.type === 'auto' ? '🚗 Auto' : p.type === 'home' ? '🏠 Inmueble' : '📄 ' + p.type}</td>
                      <td>{p.company}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{p.coverage}</td>
                      <td style={{ fontSize: 12 }}>{fmtPatron(p.endDate, 'dd/MM/yyyy')}</td>
                      <td><span className={`badge ${STATUS_CLASS[p.status]}`}>{STATUS_LABEL[p.status]}</span></td>
                      <td><button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); navigate(`/policies/${p.id}`); }}>Ver</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Siniestros del cliente */}
        {clientClaims.length > 0 && (
          <div className="card" style={{ marginTop: 16 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 12 }}>Siniestros ({clientClaims.length})</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Fecha</th><th>Descripción</th><th>Estado</th><th></th></tr></thead>
                <tbody>
                  {clientClaims.map(c => (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/claims')}>
                      <td style={{ fontSize: 12 }}>{c.date ? fmtPatron(c.date, 'dd/MM/yyyy') : '—'}</td>
                      <td style={{ fontSize: 13 }}>{c.description || c.title || '—'}</td>
                      <td><span className="badge">{c.status || 'abierto'}</span></td>
                      <td><button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); navigate('/claims'); }}>Ver</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Deudas del cliente */}
        {clientDebts.length > 0 && (
          <div className="card" style={{ marginTop: 16, borderLeft: '3px solid var(--red)' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 12 }}>Deudas / Avisos ({clientDebts.length})</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Vencimiento</th><th>Póliza</th><th>Monto</th><th>Estado</th></tr></thead>
                <tbody>
                  {clientDebts.map(d => (
                    <tr key={d.id}>
                      <td style={{ fontSize: 12 }}>{d.dueDate ? fmtPatron(d.dueDate, 'dd/MM/yyyy') : '—'}</td>
                      <td style={{ fontSize: 12 }}>{d.policyNumber || '—'}</td>
                      <td style={{ fontSize: 13 }}>{d.currency || '$'} {d.amount}</td>
                      <td><span className="badge" style={{ background: d.status === 'pending' ? 'var(--red-dim)' : 'var(--accent-dim)', color: d.status === 'pending' ? 'var(--red)' : 'var(--accent)' }}>{d.status === 'pending' ? 'Pendiente' : 'Pagada'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Cotizaciones del cliente */}
        {clientQuotes.length > 0 && (
          <div className="card" style={{ marginTop: 16 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 12 }}>Cotizaciones ({clientQuotes.length})</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Fecha</th><th>Bien</th><th>Estado</th><th></th></tr></thead>
                <tbody>
                  {clientQuotes.map(q => (
                    <tr key={q.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/quotes')}>
                      <td style={{ fontSize: 12 }}>{q.createdAt ? fmtPatron(q.createdAt, 'dd/MM/yyyy') : '—'}</td>
                      <td style={{ fontSize: 13 }}>{q.bien || q.tipo || '—'}</td>
                      <td><span className="badge">{q.status || 'abierta'}</span></td>
                      <td><button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); navigate('/quotes'); }}>Ver</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editModal && <EditClientModal client={client} onClose={() => setEditModal(false)} />}
      {policyModal && <PolicyModal clientId={id} onClose={() => setPolicyModal(false)} />}
      {mergeModal && <MergeModal client={client} onClose={() => setMergeModal(false)} />}
    </div>
  );
}
